import { Injectable } from '@angular/core';
import { waitForDrawnFrameOrTimeoutAsync } from 'services/browser-utils';
import { LoggingService } from 'services/logging.service';
import { TelemetryService } from 'services/telemetry.service';
import { hidePageForTwoDrawnFramesAsync, ResumeRepaintFlow, ResumeRepaintPorts } from 'services/resume-repaint';

/**
 * #4710 — the browser side of "the app came back showing nothing".
 *
 * This class is only the wiring. Every rule — how long an absence has to be, when
 * the repair runs relative to the measurement, what gets reported — is in
 * `ResumeRepaintFlow` (services/resume-repaint.ts), which has no Angular in it so
 * the unit-test runner can load it. Read that file's header for what the bug is
 * and why the fix has the shape it has.
 *
 * What lives HERE is the single touch of the real page, and the guarantee that
 * matters most about it: the page is always put back.
 */
@Injectable({ providedIn: 'root' })
export class ResumeRepaintService {
  private readonly flow: ResumeRepaintFlow;

  constructor(
    private logging: LoggingService,
    private telemetry: TelemetryService,
  ) {
    const ports: ResumeRepaintPorts = {
      nowMs: () => performance.now(),
      isDocumentVisible: () => !document.hidden,
      isPageTransitionRunning: () => document.querySelector('cs-nav.page-transition-active') !== null,

      requestFrame: (onFrame) => { requestAnimationFrame(() => onFrame()); },

      // raw setTimeout, not safeTimeout: this service is created once and lives for
      // the whole session, so there is no destroyed state for a late timer to reach.
      setTimer: (delayMs, action) => setTimeout(action, delayMs) as unknown as number,
      clearTimer: (handle) => clearTimeout(handle),

      repaintActivePageAsync: () => this.repaintActivePageAsync(),

      trackEvent: (name, properties) => this.telemetry.trackEvent(name, properties),
      trackSilentProblem: (name, context) => this.logging.trackSilentProblem(name, context),
    };

    this.flow = new ResumeRepaintFlow(ports);

    // `visibilitychange` directly, and deliberately NOT the app's focus event.
    // FocusStateService publishes that event for plain window blur and focus as
    // well as for visibility, so on desktop it fires when the user clicks into
    // another window while our page stays fully on screen. Acting on that would
    // hide and redraw a page the browser never stopped drawing — a visible
    // flicker on every alt-tab back — and it would make `hidden_ms` measure time
    // unfocused rather than time off screen, which is not what it reports.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flow.onHidden();
      } else {
        this.flow.onVisible();
      }
    });
  }

  /** Make the browser redraw the page the user is looking at.
   *
   * `visibility: hidden` and not `display: none`, deliberately. Both force a repaint,
   * but `display: none` takes the page out of the layout, and every scroll box inside
   * it comes back scrolled to the top — which is exactly why the sibling repair in
   * `cs-tab-content` (#4410) had to be opted into one panel at a time. `visibility`
   * keeps the layout and keeps the scroll position, so it is safe to do to whatever
   * page happens to be open.
   *
   * How many frames to wait and when to put the page back are NOT decided here —
   * both are easy to get wrong and impossible to notice afterwards, so they live in
   * `hidePageForTwoDrawnFramesAsync`, which has its own unit tests. All this method
   * does is find the page and supply a capped frame wait.
   */
  private async repaintActivePageAsync(): Promise<void> {
    const page = document.querySelector('.cs-page.active') as HTMLElement | null;
    // no page on screen yet (still booting, or between pages) — nothing to redraw
    if (!page) return;

    await hidePageForTwoDrawnFramesAsync(
      (value) => { page.style.visibility = value; },
      waitForDrawnFrameOrTimeoutAsync,
    );
  }
}
